import { useState, useCallback, useEffect } from 'react';

interface FilterOption {
  value: string;
  label: string;
}

interface SearchFilterProps {
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  filters?: Array<{
    key: string;
    label: string;
    options: FilterOption[];
    value?: string;
  }>;
  onFilterChange?: (key: string, value: string) => void;
  onClear?: () => void;
  className?: string;
}

export function SearchFilter({
  searchPlaceholder = 'Search...',
  searchValue = '',
  onSearchChange,
  filters = [],
  onFilterChange,
  onClear,
  className = '',
}: SearchFilterProps): JSX.Element {
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);
  const [searchTimeout, setSearchTimeout] = useState<number | null>(null);

  // Debounced search
  const handleSearchChange = useCallback((value: string) => {
    setLocalSearchValue(value);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      if (onSearchChange) {
        onSearchChange(value);
      }
    }, 300);

    setSearchTimeout(timeout as unknown as number);
  }, [onSearchChange, searchTimeout]);

  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const handleClear = useCallback(() => {
    setLocalSearchValue('');
    if (onSearchChange) {
      onSearchChange('');
    }
    if (onClear) {
      onClear();
    }
  }, [onSearchChange, onClear]);

  const hasActiveFilters = localSearchValue || filters.some(filter => filter.value);

  return (
    <div className={`search-filter-container ${className}`}>
      <div className="row">
        {/* Search Input */}
        <div className="col-12 col-md-8 col-lg-6 mb-3">
          <div className="input-group input-group-lg d-md-none">
            <div className="input-group-prepend">
              <span className="input-group-text">
                <i className="fas fa-search"></i>
              </span>
            </div>
            <input
              type="text"
              className="form-control"
              placeholder={searchPlaceholder}
              value={localSearchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            {localSearchValue && (
              <div className="input-group-append">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => handleSearchChange('')}
                  title="Clear search"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}
          </div>
          
          <div className="input-group d-none d-md-flex">
            <div className="input-group-prepend">
              <span className="input-group-text">
                <i className="fas fa-search"></i>
              </span>
            </div>
            <input
              type="text"
              className="form-control"
              placeholder={searchPlaceholder}
              value={localSearchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            {localSearchValue && (
              <div className="input-group-append">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => handleSearchChange('')}
                  title="Clear search"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Filter Dropdowns */}
        {filters.map((filter) => (
          <div key={filter.key} className="col-12 col-md-6 col-lg-3 mb-3">
            <select
              className="form-control form-control-lg d-md-none"
              value={filter.value || ''}
              onChange={(e) => onFilterChange && onFilterChange(filter.key, e.target.value)}
            >
              <option value="">{filter.label}</option>
              {filter.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              className="form-control d-none d-md-block"
              value={filter.value || ''}
              onChange={(e) => onFilterChange && onFilterChange(filter.key, e.target.value)}
            >
              <option value="">{filter.label}</option>
              {filter.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        ))}

        {/* Clear All Button */}
        {hasActiveFilters && (
          <div className="col-12 col-md-auto mb-3">
            <button
              className="btn btn-outline-secondary btn-lg btn-block d-md-none"
              onClick={handleClear}
            >
              <i className="fas fa-times mr-2"></i>
              Clear All Filters
            </button>
            <button
              className="btn btn-outline-secondary d-none d-md-inline-block"
              onClick={handleClear}
              title="Clear all filters"
            >
              <i className="fas fa-times mr-2"></i>
              Clear All
            </button>
          </div>
        )}
      </div>
    </div>
  );
}